import React, { useEffect, useState } from 'react';
import { Table, Tag, Typography, Select, Button, Alert, Card, Modal, Form, Input, DatePicker, message, Drawer, Descriptions } from 'antd';
import { PlusOutlined, CheckCircleOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { getProjects, getRFIs, getUsers, createRFI, respondRFI } from '../services/api';

const { Title, Text } = Typography;
const { TextArea } = Input;

interface Project {
  id: string;
  code: string;
  name: string;
}

interface UserOption {
  id: string;
  full_name: string;
}

const priorityColor = (priority: string) => {
  switch (priority) {
    case 'CRITICAL': return 'red';
    case 'HIGH': return 'orange';
    case 'MEDIUM': return 'blue';
    case 'LOW': return 'default';
    default: return 'default';
  }
};

const RFIPage: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState<string>('');
  const [rfis, setRfis] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();
  const [users, setUsers] = useState<UserOption[]>([]);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedRFI, setSelectedRFI] = useState<any>(null);
  const [responseText, setResponseText] = useState('');
  const [responding, setResponding] = useState(false);

  useEffect(() => {
    loadProjects();
    loadUsers();
  }, []);

  const loadProjects = async () => {
    try {
      const response = await getProjects();
      setProjects(response.data);
      if (response.data.length > 0) {
        setSelectedProject(response.data[0].id);
        loadRFIs(response.data[0].id);
      }
    } catch (err) {
      setError('Failed to load projects.');
    }
  };

  const loadUsers = async () => {
    try {
      const usersRes = await getUsers();
      setUsers(usersRes.data);
    } catch (err) {
      // Non-blocking — names simply won't resolve if this fails
    }
  };

  const loadRFIs = async (projectId: string) => {
    setLoading(true);
    try {
      const response = await getRFIs(projectId);
      setRfis(response.data);
    } catch (err) {
      setError('Failed to load RFIs.');
    } finally {
      setLoading(false);
    }
  };

  const handleProjectChange = (value: string) => {
    setSelectedProject(value);
    loadRFIs(value);
  };

  const userName = (id: string) => users.find(u => u.id === id)?.full_name || id;

  const openDrawer = (record: any) => {
    setSelectedRFI(record);
    setResponseText('');
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setSelectedRFI(null);
    setResponseText('');
  };

  const handleRespond = async () => {
    if (!selectedRFI) return;
    if (!responseText.trim()) {
      message.error('Response text is required.');
      return;
    }
    setResponding(true);
    try {
      await respondRFI(selectedRFI.id, { response_text: responseText });
      message.success('Response recorded and RFI closed.');
      closeDrawer();
      loadRFIs(selectedProject);
    } catch (err: any) {
      const apiMessage = err?.response?.data?.detail || 'Failed to record response.';
      message.error(apiMessage);
    } finally {
      setResponding(false);
    }
  };

  const openModal = () => {
    form.resetFields();
    setModalOpen(true);
    if (users.length === 0) {
      loadUsers();
    }
  };

  const closeModal = () => {
    setModalOpen(false);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      const payload = {
        question_text: values.question_text,
        priority: values.priority,
        drawing_ref: values.drawing_ref,
        drawing_revision: values.drawing_revision,
        raised_to: values.raised_to,
        required_by_date: values.required_by_date ? values.required_by_date.format('YYYY-MM-DD') : null,
      };
      await createRFI(selectedProject, payload);
      message.success('RFI raised successfully.');
      setModalOpen(false);
      loadRFIs(selectedProject);
    } catch (err: any) {
      if (err?.errorFields) {
        return;
      }
      const apiMessage = err?.response?.data?.detail || err?.response?.data?.question_text?.[0] || 'Failed to raise RFI.';
      message.error(apiMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const columns = [
    {
      title: 'RFI ID',
      dataIndex: ['workflow_object', 'object_id'],
      key: 'object_id',
      render: (text: string) => <Text strong style={{ color: '#1B2A4A' }}>{text}</Text>,
    },
    {
      title: 'Question',
      dataIndex: 'question_text',
      key: 'question_text',
      ellipsis: true,
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      render: (priority: string) => <Tag color={priorityColor(priority)}>{priority}</Tag>,
    },
    {
      title: 'Status',
      dataIndex: ['workflow_object', 'status'],
      key: 'status',
      render: (statusVal: string) => statusVal === 'CLOSED'
        ? <Tag icon={<CheckCircleOutlined />} color="green">Closed</Tag>
        : <Tag icon={<ClockCircleOutlined />} color="orange">{statusVal?.replace('_', ' ') || 'Open'}</Tag>,
    },
    {
      title: 'Required By',
      dataIndex: 'required_by_date',
      key: 'required_by_date',
      render: (date: string) => date ? new Date(date).toLocaleDateString('en-IN') : '—',
    },
    {
      title: 'Raised On',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => new Date(date).toLocaleDateString('en-IN'),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Title level={4} style={{ margin: 0, color: '#1B2A4A' }}>
          RFI Register
        </Title>
        <div style={{ display: 'flex', gap: 12 }}>
          <Select
            value={selectedProject}
            onChange={handleProjectChange}
            style={{ width: 250 }}
            options={projects.map(p => ({ value: p.id, label: `${p.code} — ${p.name}` }))}
          />
          <Button type="primary" icon={<PlusOutlined />} style={{ background: '#1B2A4A' }} onClick={openModal} disabled={!selectedProject}>
            Raise RFI
          </Button>
        </div>
      </div>

      {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 24 }} />}

      <Card style={{ borderRadius: 12 }}>
        <Table
          columns={columns}
          dataSource={rfis}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
          locale={{ emptyText: 'No RFIs found for this project.' }}
          onRow={(record) => ({
            onClick: () => openDrawer(record),
            style: { cursor: 'pointer' },
          })}
        />
      </Card>

      <Drawer
        title={selectedRFI?.workflow_object?.object_id || 'RFI Details'}
        open={drawerOpen}
        onClose={closeDrawer}
        width={520}
      >
        {selectedRFI && (
          <>
            <div style={{ marginBottom: 16 }}>
              <Tag color={priorityColor(selectedRFI.priority)}>{selectedRFI.priority}</Tag>
              {selectedRFI.workflow_object?.status === 'CLOSED' ? (
                <Tag icon={<CheckCircleOutlined />} color="green">Closed</Tag>
              ) : (
                <Tag icon={<ClockCircleOutlined />} color="orange">{selectedRFI.workflow_object?.status?.replace('_', ' ') || 'Open'}</Tag>
              )}
            </div>
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label="Question">
                <span style={{ whiteSpace: 'pre-wrap' }}>{selectedRFI.question_text || '—'}</span>
              </Descriptions.Item>
              <Descriptions.Item label="Drawing Reference">
                {selectedRFI.drawing_ref ? `${selectedRFI.drawing_ref}${selectedRFI.drawing_revision ? ' Rev ' + selectedRFI.drawing_revision : ''}` : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Raised To">
                {selectedRFI.raised_to ? userName(selectedRFI.raised_to) : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Required By">
                {selectedRFI.required_by_date ? new Date(selectedRFI.required_by_date).toLocaleDateString('en-IN') : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Raised On">
                {selectedRFI.created_at ? new Date(selectedRFI.created_at).toLocaleDateString('en-IN') : '—'}
              </Descriptions.Item>
              {selectedRFI.response_text && (
                <>
                  <Descriptions.Item label="Response">
                    <span style={{ whiteSpace: 'pre-wrap' }}>{selectedRFI.response_text}</span>
                  </Descriptions.Item>
                  <Descriptions.Item label="Responded By">
                    {selectedRFI.responded_by ? userName(selectedRFI.responded_by) : '—'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Responded On">
                    {selectedRFI.responded_at ? new Date(selectedRFI.responded_at).toLocaleDateString('en-IN') : '—'}
                  </Descriptions.Item>
                </>
              )}
            </Descriptions>

            {selectedRFI.workflow_object?.status !== 'CLOSED' && (
              <div style={{ marginTop: 24 }}>
                <Text strong>Record Response</Text>
                <TextArea
                  rows={4}
                  style={{ marginTop: 8 }}
                  value={responseText}
                  onChange={(e) => setResponseText(e.target.value)}
                  placeholder="Enter the response to this RFI — this will close the RFI once submitted."
                />
                <Button
                  type="primary"
                  style={{ marginTop: 12, background: '#1B2A4A' }}
                  loading={responding}
                  onClick={handleRespond}
                >
                  Submit Response & Close
                </Button>
              </div>
            )}
          </>
        )}
      </Drawer>

      <Modal
        title="Raise RFI"
        open={modalOpen}
        onCancel={closeModal}
        onOk={handleSubmit}
        confirmLoading={submitting}
        okText="Raise RFI"
        width={600}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="question_text"
            label="Question"
            rules={[{ required: true, message: 'Question text is required' }]}
          >
            <TextArea rows={4} placeholder="Describe the design query or clarification needed" />
          </Form.Item>

          <div style={{ display: 'flex', gap: 16 }}>
            <Form.Item
              name="priority"
              label="Priority"
              rules={[{ required: true, message: 'Priority is required' }]}
              style={{ flex: 1 }}
              initialValue="MEDIUM"
            >
              <Select
                options={[
                  { value: 'CRITICAL', label: 'Critical' },
                  { value: 'HIGH', label: 'High' },
                  { value: 'MEDIUM', label: 'Medium' },
                  { value: 'LOW', label: 'Low' },
                ]}
              />
            </Form.Item>
            <Form.Item
              name="required_by_date"
              label="Required By (optional override)"
              style={{ flex: 1 }}
            >
              <DatePicker style={{ width: '100%' }} format="DD-MMM-YYYY" />
            </Form.Item>
          </div>

          <div style={{ display: 'flex', gap: 16 }}>
            <Form.Item name="drawing_ref" label="Drawing Reference" style={{ flex: 1 }}>
              <Input placeholder="e.g. S-101" />
            </Form.Item>
            <Form.Item name="drawing_revision" label="Revision" style={{ flex: 1 }}>
              <Input placeholder="e.g. B" />
            </Form.Item>
          </div>

          <Form.Item name="raised_to" label="Raised To (PMC Engineer)">
            <Select
              placeholder="Select responsible engineer"
              options={users.map(u => ({ value: u.id, label: u.full_name }))}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default RFIPage;